import { Form, Formik, FormikProps } from 'formik';
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as Yup from 'yup';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography
} from '@material-ui/core';
import Alert, { Color } from '@material-ui/lab/Alert';

import { FetchError } from '../../common/api/api';
import { formatDate } from '../../common/formatter';
import FormikButton from '../../common/formik/FormikButton';
import FormikGrid from '../../common/formik/FormikGrid';
import FormikTextField from '../../common/formik/FormikTextField';
import { UserContext } from '../../user/User.context';
import { usePasswordRecover } from '../../user/User.hooks';
import { authConfig } from '../auth/Auth.config';
import { AuthMethodId } from '../auth/Auth.interface';
import AuthMethodListPicker from '../auth/components/AuthMethodListPicker';
import { getGoogleAuth } from '../google/GoogleAuth';
import { useGoogleSign } from '../google/GoogleAuth.hooks';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { JiraServerContext } from '../jira-server/jira-server.context';
import { useOktaSign } from '../okta/Okta.hooks';
import { useElectronicSignature } from './ElectronicSignature.hooks';

interface FormValues {
  email: string;
  password: string;
  reason: string;
  token: string;
}

export interface ElectronicSignatureDialogProps {
  open: boolean;
  meaning: string;
  reasonLabel?: string;
  onCancel?: () => void;
  onSigned?: (reason?: string | null) => void;
}

type AlertMessage = {
  severity?: Color;
  message: React.ReactNode;
};

const MeaningAlert: React.FC<{ message: string }> = ({ message }) => {
  const success = ['approve', 'approving'].some(match => {
    return message.includes(match);
  });
  const error = message.includes('reject');
  const severity: Color = success ? 'success' : error ? 'error' : 'info';
  return <Alert severity={severity}>{message}</Alert>;
};

const ElectronicSignatureDialog: React.FC<ElectronicSignatureDialogProps> = ({
  open,
  meaning,
  reasonLabel,
  onCancel,
  onSigned
}) => {
  const formikRef = useRef<FormikProps<FormValues> | null>(null);
  const nowRef = useRef(new Date());
  const reasonRef = useRef<string | null | undefined>();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { user } = useContext(UserContext);
  const [email, setEmail] = useState<string>('');
  const [isPasswordSet, setIsPasswordSet] = useState<boolean>(
    user?.isPasswordSet ?? true
  );
  const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
  const [error, setError] = useState<FetchError | undefined>();
  const [selectedAuthMethodId, setSelectedAuthMethodId] = useState(
    authConfig.defaultMethod.id
  );
  const {
    success,
    isLoading: isRecovering,
    error: recoverError,
    doPasswordRecover
  } = usePasswordRecover();
  const { isJiraServer: isJira } = useContext(JiraServerContext);
  const { isAvailable: isConnectAvailable } = useJiraConnectAPI();
  const isAvailable: boolean = isJira || isConnectAvailable;
  const { isLoading: isLoadingGoogle, sign: googleSign } = useGoogleSign();
  const { isLoading: isLoadingOkta, sign: oktaSign } = useOktaSign();
  const {
    signed,
    isLoading: isSigning,
    error: signError,
    post
  } = useElectronicSignature();
  const isLoading = isSigning || isLoadingGoogle || isLoadingOkta;
  const isGoogleAuth = selectedAuthMethodId === AuthMethodId.Google;

  const timezone: string = useMemo(() => {
    const now: string = nowRef.current.toString();
    const splitIndex: number = now.indexOf(' ', now.lastIndexOf(':'));
    const value: string = now.slice(splitIndex, now.length);
    const timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${value} (${timezone})`;
  }, []);

  useLayoutEffect(() => {
    // render google login button when opened
    if (!open) {
      return;
    }
    (async () => {
      // add slight delay to wait for contentRef value
      await new Promise(resolve => setImmediate(resolve));
      // render the button under the .auth-method-area element
      const parent = contentRef.current?.querySelector<HTMLElement>(
        `.auth-method-list-picker [data-auth-method="${AuthMethodId.Google}"] .auth-method-area`
      );
      if (!parent) {
        return;
      }
      const googleAuth = await getGoogleAuth();
      googleAuth.renderButton({
        parent,
        onClick: () => {
          setError(undefined);
          setAlertMessage(undefined);
          setSelectedAuthMethodId(AuthMethodId.Google);
        },
        onResponse: async response => {
          const { data, error } = await googleSign({
            token: response.credential
          });
          // directly set error message since the message is transformed
          // when setting an error with 401 status
          if (error) {
            setAlertMessage({ severity: 'error', message: error.message });
          }
          // either set or unset token
          formikRef.current?.setFieldValue(
            'token',
            data?.accessToken || '',
            true
          );
        }
      });
    })();
  }, [open, googleSign]);

  // remove token and validate form when switching
  // from and to the google auth method
  useEffect(() => {
    formikRef.current?.setFieldValue('token', '', true);
  }, [isGoogleAuth]);

  useEffect(() => {
    if (!success && recoverError) {
      setError(recoverError);
    } else if (!signed && signError) {
      setError(signError);
    } else {
      //clear error
      setError(undefined);
    }
  }, [success, recoverError, signed, signError]);

  useEffect(() => {
    formikRef.current?.setSubmitting(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (signed) {
      onSigned?.(reasonRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signed]);

  useEffect(() => {
    if (
      user &&
      typeof user.isPasswordSet === 'boolean' &&
      !user.isPasswordSet
    ) {
      setIsPasswordSet(false);
    }
  }, [user, open]);

  const shouldSetupPassword = isAvailable && !isPasswordSet && !success;

  useEffect(() => {
    if (error) {
      setAlertMessage({
        severity: 'error',
        message:
          error.statusCode === 401 ? (
            <>
              Sorry, the email and password does not match. Please try again or
              click <strong>Reset Password</strong> below and we'll send a link
              to reset your password to <strong>{email}</strong>.
            </>
          ) : (
            error.message
          )
      });
    } else if (success) {
      setAlertMessage({
        severity: 'info',
        message: (
          <>
            We sent a link to reset your password to <strong>{email}</strong>.
            Please follow the instructions to set a new password then return to
            this window to continue signing.
          </>
        )
      });
    } else if (shouldSetupPassword) {
      setAlertMessage({
        severity: 'warning',
        message: (
          <>
            Please setup your password to continue with electronic signing.{' '}
            <strong>Enter your email</strong> above and click on{' '}
            <strong>Reset Password</strong> to receive an email to setup your
            password.
          </>
        )
      });
    } else {
      setAlertMessage(undefined);
    }
  }, [shouldSetupPassword, success, error, email]);

  const validationSchema = Yup.object(
    isGoogleAuth
      ? { token: Yup.string().required('Required') }
      : {
          email: Yup.string()
            .lowercase()
            .email()
            .required('Required')
            .test(
              'match-user-email',
              `Does not match the email for this account (${user?.email})`,
              value => !!(user && user.email.toLowerCase() === value)
            ),
          password: Yup.string().required('Required')
        }
  );

  return (
    <Dialog open={open} onClose={onCancel}>
      <Formik
        innerRef={formikRef}
        initialValues={{ email: '', password: '', reason: '', token: '' }}
        validationSchema={validationSchema}
        onSubmit={async values => {
          setError(undefined);
          setAlertMessage(undefined);
          const { reason, ...dto } = values;
          if (typeof reason !== 'undefined') {
            reasonRef.current = reason || null;
          }
          // handle sign
          switch (selectedAuthMethodId) {
            case AuthMethodId.Google: {
              // make sure the access token is set from google sign endpoint
              if (values.token) {
                onSigned?.(reasonRef.current);
              } else {
                setError({
                  statusCode: 0,
                  error: 'Authentication Error',
                  message: 'Your Google account was not authenticated.'
                });
              }
              break;
            }
            case AuthMethodId.Okta: {
              const { data, error } = await oktaSign(dto);
              setError(error);
              data && onSigned?.(reasonRef.current);
              break;
            }
            default:
              post(dto);
          }
        }}
      >
        {({ values }) => (
          <Form autoComplete='off'>
            <DialogTitle>Electronic Signature</DialogTitle>
            <DialogContent ref={contentRef}>
              {meaning && (
                <DialogContentText component='div'>
                  <MeaningAlert message={meaning} />
                </DialogContentText>
              )}
              {authConfig.multiple && (
                <AuthMethodListPicker
                  label='Sign using:'
                  disabled={isLoading}
                  value={selectedAuthMethodId}
                  style={{ marginBottom: 12 }}
                  // disable clicking google auth icon once authenticated
                  disableAuthMethods={
                    values.token ? [AuthMethodId.Google] : undefined
                  }
                  onChange={authMethod => {
                    if (!authMethod) {
                      return;
                    }
                    setSelectedAuthMethodId(authMethod.id);
                    if (authMethod.id !== selectedAuthMethodId) {
                      // remove errors when switching auth methods
                      setError(undefined);
                      setAlertMessage(undefined);
                    }
                  }}
                />
              )}
              <FormikGrid>
                {isGoogleAuth ? (
                  <Grid item xs={12}>
                    <div>
                      <Typography variant='body1'>
                        {/* use isValid state from props since using formikRef
                         * doesn't properly rerender the label */}
                        {values.token
                          ? 'Your Google account has been authenticated.'
                          : 'Select your Google account from the separate window to sign.'}
                      </Typography>
                    </div>
                  </Grid>
                ) : (
                  <>
                    <FormikTextField
                      autoFocus
                      type='text'
                      name='email'
                      label='Email'
                      grid={{ xs: 12, md: shouldSetupPassword ? 12 : 6 }}
                      onChange={event =>
                        setEmail(event.target.value.toLowerCase())
                      }
                    />
                    {!shouldSetupPassword && (
                      <FormikTextField
                        type='password'
                        name='password'
                        label='Password'
                        grid={{ xs: 12, md: 6 }}
                      />
                    )}
                  </>
                )}
                {reasonLabel && (
                  <FormikTextField
                    type='text'
                    name='reason'
                    label={reasonLabel}
                  />
                )}
                <Grid item xs={12}>
                  <Box>
                    <Typography variant='body2'>
                      {formatDate(nowRef.current)}
                    </Typography>
                    <Typography
                      variant='caption'
                      // display='inline'
                      color='textSecondary'
                    >
                      {timezone}
                    </Typography>
                  </Box>
                </Grid>
              </FormikGrid>
              {alertMessage && (
                <Box mt={1}>
                  <Alert severity={alertMessage.severity}>
                    {alertMessage.message}
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {/* only show reset password for Jira if in another
               * auth method other than the Google auth method */}
              {isAvailable && !isGoogleAuth && (
                <>
                  <Button
                    color='primary'
                    variant='contained'
                    onClick={() => {
                      setError(undefined);
                      setAlertMessage(undefined);
                      doPasswordRecover(email);
                    }}
                    disabled={
                      isRecovering ||
                      !validationSchema.isValidSync({ email, password: '***' })
                    }
                    endIcon={
                      isRecovering && (
                        <CircularProgress color='primary' size={20} />
                      )
                    }
                  >
                    Reset Password
                  </Button>
                  <div style={{ flex: 1 }} />
                </>
              )}
              <Button onClick={onCancel}>Cancel</Button>
              <FormikButton>Sign</FormikButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ElectronicSignatureDialog;
